import { useState } from 'react';
import { useScrollReveal } from '@/hooks/useScrollReveal';
import { modules } from '@/mocks/modules';
import ModuleModal from './ModuleModal';

const categories = ['Todos', 'Administración', 'Operación', 'Finanzas', 'Configuración', 'Seguridad', 'Online'];

export default function ModuleGallery() {
  const [activeCategory, setActiveCategory] = useState('Todos');
  const [selectedModule, setSelectedModule] = useState<typeof modules[0] | null>(null);
  const { ref: sectionRef, isVisible } = useScrollReveal<HTMLDivElement>();

  const filtered = activeCategory === 'Todos'
    ? modules
    : modules.filter((m) => m.category === activeCategory);

  return (
    <section id="modulos" className="relative py-20 md:py-28 bg-background-100 texture-grid">
      <div className="w-full px-4 md:px-8 lg:px-12">
        <div ref={sectionRef} className="max-w-7xl mx-auto">
          {/* Header */}
          <div className={`text-center mb-10 md:mb-14 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
            <span className="inline-block px-4 py-1.5 rounded-full bg-secondary-100 text-secondary-800 text-sm font-semibold mb-4">
              Conoce el sistema
            </span>
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-primary-900">
              Galería de Módulos
            </h2>
            <p className="mt-4 text-base md:text-lg text-foreground-600 max-w-2xl mx-auto">
              Explora las 12 herramientas principales de Acualys-Bo. Cada módulo está diseñado para cubrir una necesidad real de tu cooperativa de agua.
            </p>
          </div>

          {/* Category filters */}
          <div className={`flex flex-wrap items-center justify-center gap-2 mb-10 md:mb-14 transition-all duration-700 delay-150 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                  activeCategory === cat
                    ? 'bg-primary-500 text-white'
                    : 'bg-background-50 text-foreground-600 hover:bg-primary-50 hover:text-primary-600 border border-background-200'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>

          {/* Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {filtered.map((mod, i) => (
              <button
                key={mod.id}
                onClick={() => setSelectedModule(mod)}
                className={`group text-left bg-background-50 rounded-xl border border-background-200 overflow-hidden transition-all duration-500 hover:scale-[1.03] hover:border-accent-300 hover:shadow-xl cursor-pointer ${
                  isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                }`}
                style={{ transitionDelay: `${200 + i * 100}ms` }}
              >
                {/* Laptop mockup frame */}
                <div className="laptop-mockup mx-4 mt-4">
                  <div className="laptop-screen aspect-[16/10]">
                    <img
                      src={mod.image}
                      alt={mod.title}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                </div>

                {/* Content */}
                <div className="p-5 md:p-6">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="px-2.5 py-0.5 rounded-full bg-accent-100 text-accent-800 text-xs font-semibold">
                      {mod.category}
                    </span>
                  </div>
                  <h3 className="font-heading text-base md:text-lg font-semibold text-foreground-900 group-hover:text-primary-700 transition-colors">
                    {mod.title}
                  </h3>
                  <p className="mt-1.5 text-sm text-foreground-600 leading-relaxed line-clamp-2">
                    {mod.description}
                  </p>
                  <div className="mt-3 flex items-center gap-1 text-accent-600 text-sm font-medium opacity-0 group-hover:opacity-100 transition-opacity">
                    <span>Ver detalle</span>
                    <i className="ri-arrow-right-line" />
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Modal */}
      <ModuleModal
        module={selectedModule}
        onClose={() => setSelectedModule(null)}
      />
    </section>
  );
}